/**
 * This is the top-level component that defines your UI application.
 *
 * This is an appropriate spot for application wide components and configuration,
 * stuff like application chrome (headers, footers, navigation, etc), routing
 * (what urls go where), etc.
 *
 * @see https://github.com/reactjs/react-router-tutorial/tree/master/lessons
 */

import { useState, useEffect } from 'react';
import {
    styled,
    Box,
    List,
    Drawer,
    ListItem,
    ListItemButton,
    Typography,
    Divider,
    Toolbar,
    Button,
    Link,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Route, Routes } from 'react-router-dom';
import { Footer } from '@allenai/varnish2';
import AddIcon from '@mui/icons-material/Add';

import { Home } from './pages/Home';
import { Landing } from './pages/Landing';
import { Thread } from './types/Thread';
import { loadThreads, getThread } from './utils/threadStorage';
import { TermsAndConditionsModal } from './components/TermsAndConditionsModal';
import { checkTermsAcceptance, recordTermsAcceptance } from './api/TermsAcceptance';
import AuthButton from './components/AuthButton';
// import { About } from './pages/About';

const DRAWER_WIDTH = 280;
const MOBILE_DRAWER_WIDTH = 100;

// Authenticated App Component - only renders when on /app route
const AuthenticatedApp = () => {
    // Terms acceptance state
    const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
    const [isCheckingTerms, setIsCheckingTerms] = useState<boolean>(true);

    // Thread management state
    const [threads, setThreads] = useState<Thread[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    const [focusPaperTitleInput, setFocusPaperTitleInput] = useState<boolean>(false);

    // Responsive design
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Check terms acceptance on mount
    useEffect(() => {
        const checkTerms = async () => {
            try {
                const acceptance = await checkTermsAcceptance();
                setTermsAccepted(acceptance.accepted);
            } catch (error) {
                console.error('Error checking terms acceptance:', error);
                // If error checking, assume not accepted to be safe
                setTermsAccepted(false);
            } finally {
                setIsCheckingTerms(false);
            }
        };
        checkTerms();
    }, []);

    // Load threads from database on mount (only after terms accepted)
    useEffect(() => {
        if (termsAccepted !== true) {
            return; // Don't load threads until terms accepted
        }

        const fetchThreads = async () => {
            const loadedThreads = await loadThreads();
            setThreads(loadedThreads);
            
            // Check if there's a corpus_id in the URL - if so, don't auto-select a thread
            const urlParams = new URLSearchParams(window.location.search);
            const corpusIdFromUrl = urlParams.get('corpus_id');
            
            if (corpusIdFromUrl) {
                // URL corpus_id present - start with no active thread (new thread view)
                setActiveThreadId(null);
            } else if (loadedThreads.length > 0) {
                // No URL corpus_id - set the most recently updated thread as active
                const sorted = [...loadedThreads].sort((a, b) => b.updatedAt - a.updatedAt);
                if (sorted[0]) {
                    setActiveThreadId(sorted[0].id);
                }
            }
        };
        fetchThreads();
    }, [termsAccepted]);

    // Fetch full thread details when activeThreadId changes
    useEffect(() => {
        const fetchActiveThread = async () => {
            if (activeThreadId) {
                const fullThread = await getThread(activeThreadId);
                if (fullThread) {
                    // Update the threads array with the full thread
                    setThreads(prev => 
                        prev.map(t => t.id === activeThreadId ? fullThread : t)
                    );
                } else {
                    console.warn('Failed to fetch full thread:', activeThreadId);
                }
            }
        };
        fetchActiveThread();
    }, [activeThreadId]);

    const handleSelectThread = (threadId: string) => {
        setActiveThreadId(threadId);
    };

    const handleNewThread = () => {
        setActiveThreadId(null);
        setFocusPaperTitleInput(true);
    };

    const handleThreadsUpdate = async () => {
        const loadedThreads = await loadThreads();
        
        // Update threads but preserve messages only for the active thread
        setThreads(prev => {
            return loadedThreads.map(freshThread => {
                // Only preserve messages for the currently active thread
                const existingThread = prev.find(t => t.id === freshThread.id);
                if (existingThread && existingThread.id === activeThreadId && existingThread.messages.length > 0) {
                    // Preserve messages from active thread, update metadata
                    return {
                        ...freshThread,
                        messages: existingThread.messages
                    };
                }
                // For non-active threads, clear messages so they'll be refetched when selected
                return freshThread;
            });
        });
    };

    const handleUpdateThreadInPlace = (updatedThread: Thread) => {
        setThreads(prev => {
            const exists = prev.some(t => t.id === updatedThread.id);
            if (exists) {
                return prev.map(t => t.id === updatedThread.id ? updatedThread : t);
            } else {
                // New thread, add it
                return [updatedThread, ...prev];
            }
        });
    };

    const handleTermsAccept = async (openDatasets: boolean) => {
        try {
            await recordTermsAcceptance(openDatasets);
            setTermsAccepted(true);
        } catch (error) {
            console.error('Error recording terms acceptance:', error);
            throw error; // Re-throw to let modal handle it
        }
    };

    const activeThread = threads.find(t => t.id === activeThreadId) || null;

    // Show loading state while checking terms
    if (isCheckingTerms) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    bgcolor: (t) => t.palette.background.default,
                }}
            >
                <CircularProgress sx={{ color: '#0FCB8C' }} />
            </Box>
        );
    }

    // Render the main app (always show it, even when modal is active)
    const appContent = (
        <Box sx={{ display: 'flex', bgcolor: (t) => t.palette.background.default, color: (t) => t.palette.text.primary }}>
            {/* Permanent Left Drawer */}
            <Drawer
                variant="permanent"
                anchor="left"
                sx={{
                    width: { xs: MOBILE_DRAWER_WIDTH, sm: DRAWER_WIDTH },
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: { xs: MOBILE_DRAWER_WIDTH, sm: DRAWER_WIDTH },
                        boxSizing: 'border-box',
                        bgcolor: '#0A3235',
                        color: '#FFFFFF',
                    },
                }}
                aria-label="Main navigation">
                <Toolbar>
                    {/* Logo Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: '100%',
                            py: 1,
                        }}>
                        <Logo>
                            <svg viewBox="0 0 953 243" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M49.6965 53.7493H24.8482V29.7241H44.8516C47.5223 29.7241 49.6965 27.5142 49.6965 24.7996V4.46777H73.3335V29.7241C73.3335 42.9955 62.7534 53.7493 49.6965 53.7493ZM24.8482 56.2115H0V80.2367H20.0033C22.6741 80.2367 24.8482 82.4466 24.8482 85.1612V105.493H48.4852V80.2367C48.4852 66.9653 37.9052 56.2115 24.8482 56.2115ZM104.238 54.9804C101.567 54.9804 99.3929 52.7706 99.3929 50.0559V29.7241H75.7559V54.9804C75.7559 68.2518 86.3359 79.0056 99.3929 79.0056H124.241V54.9804H104.238ZM50.9077 105.493V130.749H74.5447V110.417C74.5447 107.703 76.7188 105.493 79.3896 105.493H99.3929V81.4678H74.5447C61.4877 81.4678 50.9077 92.2216 50.9077 105.493Z" fill="#0FCB8C"/>
                                <path d="M886.425 100.657C886.803 106.826 889.195 111.799 893.602 115.576C898.008 119.353 904.555 121.242 913.242 121.242C921.174 121.242 926.65 120.046 929.672 117.654C932.694 115.136 934.204 111.484 934.204 106.7C934.204 102.797 933.008 99.7758 930.616 97.6355C928.35 95.3693 924.762 93.8585 919.852 93.1031L896.434 89.5149C888.251 88.2559 881.956 85.4232 877.549 81.0167C873.269 76.6102 871.128 70.8188 871.128 63.6425C871.128 54.5777 874.591 47.4643 881.515 42.3024C888.44 37.1405 898.071 34.5596 910.409 34.5596C922.37 34.5596 932.001 37.3294 939.303 42.869C946.606 48.2827 950.571 55.5219 951.201 64.5867H932.316C931.812 59.9284 929.672 56.0885 925.895 53.0669C922.118 50.0453 916.767 48.5345 909.843 48.5345C902.918 48.5345 897.756 49.7935 894.357 52.3115C891.084 54.7036 889.447 57.914 889.447 61.9428C889.447 65.0903 890.391 67.6713 892.28 69.6857C894.294 71.7001 897.693 73.085 902.478 73.8404L922.873 76.862C933.953 78.4987 941.695 81.7721 946.102 86.6822C950.508 91.4664 952.712 97.8243 952.712 105.756C952.712 115.576 949.438 122.941 942.892 127.851C936.345 132.762 926.273 135.217 912.676 135.217C897.819 135.217 886.803 132.069 879.627 125.774C872.45 119.353 868.862 110.981 868.862 100.657H886.425Z" fill="#0FCB8C"/>
                                <path d="M811.637 34.7484C826.115 34.7484 837.069 39.4697 844.497 48.9122C851.925 58.2288 855.639 70.2522 855.639 84.9825C855.639 99.7128 851.925 111.799 844.497 121.242C837.069 130.558 826.115 135.217 811.637 135.217C795.018 135.217 783.372 128.796 776.699 115.954V132.195H757.814V0H776.699V54.0111C783.372 41.1693 795.018 34.7484 811.637 34.7484ZM807.671 49.8564C787.023 49.8564 776.699 61.5651 776.699 84.9825C776.699 108.4 787.023 120.109 807.671 120.109C817.995 120.109 825.36 117.276 829.766 111.61C834.299 105.819 836.565 96.943 836.565 84.9825C836.565 73.022 834.299 64.209 829.766 58.5435C825.36 52.7521 817.995 49.8564 807.671 49.8564Z" fill="#0FCB8C"/>
                                <path d="M682.618 134.65C672.923 134.65 665.243 132.195 659.578 127.285C654.038 122.375 651.269 115.576 651.269 106.889C651.269 99.0833 653.472 92.8513 657.878 88.193C662.411 83.4088 669.776 80.1354 679.974 78.3728L703.58 74.4069C708.238 73.6515 711.575 72.4555 713.589 70.8188C715.729 69.1821 716.8 66.727 716.8 63.4536C716.8 58.7953 715.226 55.2701 712.078 52.878C709.057 50.36 703.706 49.101 696.026 49.101C688.346 49.101 682.555 50.6118 678.652 53.6334C674.875 56.655 672.735 61.1874 672.231 67.2306H653.346C653.472 57.0327 657.186 49.0381 664.488 43.2467C671.79 37.4553 682.051 34.5596 695.271 34.5596C707.987 34.5596 717.618 37.2035 724.165 42.4913C730.837 47.7791 734.174 54.8924 734.174 63.8313V110.666C734.174 117.591 734.488 124.767 735.118 132.195H717.933C717.177 124.767 716.8 117.654 716.8 110.855C714.659 117.654 710.756 123.319 705.091 127.851C699.425 132.384 691.934 134.65 682.618 134.65ZM686.584 120.109C696.152 120.109 703.58 116.961 708.868 110.666C714.156 104.245 716.8 96.1247 716.8 86.3045V80.639C714.533 82.2757 712.078 83.5976 709.434 84.6048C706.79 85.4861 703.643 86.3674 699.992 87.2487L686.961 90.2703C681.044 91.6552 676.763 93.6067 674.119 96.1247C671.476 98.5168 670.154 101.853 670.154 106.134C670.154 110.54 671.601 114.002 674.497 116.52C677.519 118.913 681.548 120.109 686.584 120.109Z" fill="#0FCB8C"/>
                                <path d="M558.799 6.61035H578.25V116.521H641.893V132.196H558.799V6.61035Z" fill="#0FCB8C"/>
                                <path d="M484.546 134.65C474.852 134.65 467.172 132.195 461.507 127.285C455.967 122.375 453.197 115.576 453.197 106.889C453.197 99.0833 455.401 92.8513 459.807 88.193C464.339 83.4088 471.705 80.1354 481.902 78.3728L505.509 74.4069C510.167 73.6515 513.503 72.4555 515.518 70.8188C517.658 69.1821 518.728 66.727 518.728 63.4536C518.728 58.7953 517.154 55.2701 514.007 52.878C510.985 50.36 505.635 49.101 497.955 49.101C490.275 49.101 484.483 50.6118 480.581 53.6334C476.804 56.655 474.663 61.1874 474.16 67.2306H455.275C455.401 57.0327 459.115 49.0381 466.417 43.2467C473.719 37.4553 483.98 34.5596 497.199 34.5596C509.915 34.5596 519.547 37.2035 526.093 42.4913C532.766 47.7791 536.102 54.8924 536.102 63.8313V110.666C536.102 117.591 536.417 124.767 537.047 132.195H519.861C519.106 124.767 518.728 117.654 518.728 110.855C516.588 117.654 512.685 123.319 507.02 127.851C501.354 132.384 493.863 134.65 484.546 134.65ZM488.512 120.109C498.081 120.109 505.509 116.961 510.797 110.666C516.084 104.245 518.728 96.1247 518.728 86.3045V80.639C516.462 82.2757 514.007 83.5976 511.363 84.6048C508.719 85.4861 505.572 86.3674 501.921 87.2487L488.89 90.2703C482.973 91.6552 478.692 93.6067 476.048 96.1247C473.404 98.5168 472.082 101.853 472.082 106.134C472.082 110.54 473.53 114.002 476.426 116.52C479.447 118.913 483.476 120.109 488.512 120.109Z" fill="#0FCB8C"/>
                                <path d="M419.46 132.196C411.906 132.196 406.052 131.251 401.897 129.363C397.868 127.349 395.035 124.39 393.399 120.487C391.762 116.458 390.944 111.107 390.944 104.435V52.3122H373.003V42.492L392.643 35.6934L400.386 16.4307H409.829V37.7707H440.045V52.3122H409.829V117.088H438.156V132.196H419.46Z" fill="#0FCB8C"/>
                                <path d="M298.667 100.657C299.044 106.826 301.436 111.799 305.843 115.576C310.249 119.353 316.796 121.242 325.483 121.242C333.415 121.242 338.892 120.046 341.913 117.654C344.935 115.136 346.446 111.484 346.446 106.7C346.446 102.797 345.25 99.7758 342.857 97.6355C340.591 95.3693 337.003 93.8585 332.093 93.1031L308.676 89.5149C300.492 88.2559 294.197 85.4232 289.791 81.0167C285.51 76.6102 283.37 70.8188 283.37 63.6425C283.37 54.5777 286.832 47.4643 293.756 42.3024C300.681 37.1405 310.312 34.5596 322.651 34.5596C334.611 34.5596 344.242 37.3294 351.545 42.869C358.847 48.2827 362.813 55.5219 363.442 64.5867H344.557C344.054 59.9284 341.913 56.0885 338.136 53.0669C334.359 50.0453 329.008 48.5345 322.084 48.5345C315.159 48.5345 309.998 49.7935 306.598 52.3115C303.325 54.7036 301.688 57.914 301.688 61.9428C301.688 65.0903 302.632 67.6713 304.521 69.6857C306.535 71.7001 309.935 73.085 314.719 73.8404L335.115 76.862C346.194 78.4987 353.937 81.7721 358.343 86.6822C362.75 91.4664 364.953 97.8243 364.953 105.756C364.953 115.576 361.68 122.941 355.133 127.851C348.586 132.762 338.514 135.217 324.917 135.217C310.061 135.217 299.044 132.069 291.868 125.774C284.692 119.353 281.104 110.981 281.104 100.657H298.667Z" fill="#0FCB8C"/>
                                <path d="M252.976 132.196L241.833 102.357H181.024L169.882 132.196H150.241L196.887 6.61035H226.159L272.805 132.196H252.976ZM186.878 86.6828H235.979L211.429 20.3964L186.878 86.6828Z" fill="#0FCB8C"/>
                                <path d="M913.925 226.04L929.349 179.96H937.285L952.709 226.04H946.789L932.357 183.288H934.149L919.845 226.04H913.925ZM921.349 215.256V209.912H945.285V215.256H921.349Z" fill="#0FCB8C"/>
                                <path d="M907.66 226.968L895.084 214.456L898.828 210.712L911.34 223.224L907.66 226.968ZM890.444 227C885.858 227 881.964 225.987 878.764 223.96C875.564 221.933 873.122 219.117 871.436 215.512C869.772 211.907 868.94 207.736 868.94 203C868.94 198.264 869.772 194.093 871.436 190.488C873.122 186.883 875.564 184.067 878.764 182.04C881.964 180.013 885.858 179 890.444 179C895.052 179 898.956 180.013 902.156 182.04C905.356 184.067 907.788 186.883 909.452 190.488C911.116 194.093 911.948 198.264 911.948 203C911.948 207.736 911.116 211.907 909.452 215.512C907.788 219.117 905.356 221.933 902.156 223.96C898.956 225.987 895.052 227 890.444 227ZM890.444 221.56C893.879 221.581 896.738 220.813 899.02 219.256C901.324 217.677 903.042 215.491 904.172 212.696C905.324 209.901 905.9 206.669 905.9 203C905.9 199.309 905.324 196.077 904.172 193.304C903.042 190.531 901.324 188.365 899.02 186.808C896.738 185.251 893.879 184.461 890.444 184.44C887.01 184.419 884.151 185.187 881.868 186.744C879.607 188.301 877.9 190.477 876.748 193.272C875.596 196.067 875.01 199.309 874.988 203C874.967 206.669 875.532 209.891 876.684 212.664C877.836 215.437 879.554 217.613 881.836 219.192C884.14 220.749 887.01 221.539 890.444 221.56Z" fill="#0FCB8C"/>
                                <path d="M835.851 227.001C832.459 227.001 829.494 226.254 826.955 224.761C824.438 223.246 822.475 221.145 821.067 218.457C819.659 215.747 818.955 212.59 818.955 208.985C818.955 205.209 819.648 201.945 821.035 199.193C822.422 196.419 824.352 194.286 826.827 192.793C829.323 191.278 832.246 190.521 835.595 190.521C839.072 190.521 842.027 191.321 844.459 192.921C846.912 194.521 848.747 196.803 849.963 199.769C851.2 202.734 851.734 206.265 851.563 210.361H845.803V208.313C845.739 204.067 844.875 200.899 843.211 198.809C841.547 196.697 839.072 195.641 835.787 195.641C832.267 195.641 829.59 196.771 827.755 199.033C825.92 201.294 825.003 204.537 825.003 208.761C825.003 212.835 825.92 215.993 827.755 218.233C829.59 220.473 832.203 221.593 835.595 221.593C837.878 221.593 839.862 221.07 841.547 220.025C843.232 218.979 844.555 217.475 845.515 215.513L850.987 217.401C849.643 220.451 847.616 222.819 844.907 224.505C842.219 226.169 839.2 227.001 835.851 227.001ZM823.083 210.361V205.785H848.619V210.361H823.083Z" fill="#0FCB8C"/>
                                <path d="M797.625 226.04V191.48H802.713V199.832L801.881 198.744C802.286 197.677 802.809 196.696 803.449 195.8C804.11 194.904 804.846 194.168 805.657 193.592C806.553 192.866 807.566 192.312 808.697 191.928C809.828 191.544 810.98 191.32 812.153 191.256C813.326 191.17 814.425 191.245 815.449 191.48V196.824C814.254 196.504 812.942 196.418 811.513 196.568C810.084 196.717 808.761 197.24 807.545 198.136C806.436 198.925 805.572 199.885 804.953 201.016C804.356 202.146 803.94 203.373 803.705 204.696C803.47 205.997 803.353 207.33 803.353 208.696V226.04H797.625Z" fill="#0FCB8C"/>
                                <path d="M772.058 226.936C769.839 226.936 767.93 226.584 766.33 225.88C764.751 225.176 763.439 224.238 762.394 223.064C761.349 221.891 760.527 220.59 759.93 219.16C759.333 217.731 758.906 216.27 758.65 214.776C758.415 213.283 758.298 211.886 758.298 210.584V191.48H764.058V208.792C764.058 210.414 764.207 211.982 764.506 213.496C764.805 215.011 765.306 216.376 766.01 217.592C766.735 218.808 767.695 219.768 768.89 220.472C770.106 221.176 771.621 221.528 773.434 221.528C774.863 221.528 776.165 221.283 777.338 220.792C778.511 220.302 779.514 219.576 780.346 218.616C781.178 217.635 781.818 216.398 782.266 214.904C782.714 213.411 782.938 211.672 782.938 209.688L786.682 210.84C786.682 214.168 786.074 217.038 784.858 219.448C783.663 221.859 781.967 223.715 779.77 225.016C777.594 226.296 775.023 226.936 772.058 226.936ZM783.578 226.04V216.792H782.938V191.48H788.666V226.04H783.578Z" fill="#0FCB8C"/>
                                <path d="M734.547 242.361C732.733 242.361 730.963 242.083 729.235 241.529C727.507 240.974 725.917 240.131 724.467 239.001C723.037 237.891 721.843 236.483 720.883 234.777L726.099 232.025C726.952 233.71 728.157 234.937 729.715 235.705C731.293 236.473 732.915 236.857 734.579 236.857C736.776 236.857 738.6 236.451 740.051 235.641C741.501 234.851 742.568 233.667 743.251 232.089C743.955 230.51 744.296 228.547 744.275 226.201V216.761H744.947V191.481H750.003V226.265C750.003 227.203 749.971 228.11 749.907 228.985C749.864 229.859 749.757 230.723 749.587 231.577C749.139 234.03 748.253 236.046 746.931 237.625C745.608 239.225 743.901 240.409 741.811 241.177C739.72 241.966 737.299 242.361 734.547 242.361ZM734.131 227.001C730.909 227.001 728.147 226.201 725.843 224.601C723.539 222.979 721.757 220.793 720.499 218.041C719.261 215.289 718.643 212.185 718.643 208.729C718.643 205.273 719.261 202.169 720.499 199.417C721.757 196.665 723.539 194.499 725.843 192.921C728.147 191.321 730.899 190.521 734.099 190.521C737.363 190.521 740.104 191.31 742.323 192.889C744.541 194.467 746.216 196.633 747.347 199.385C748.499 202.137 749.075 205.251 749.075 208.729C749.075 212.163 748.499 215.267 747.347 218.041C746.216 220.793 744.541 222.979 742.323 224.601C740.104 226.201 737.373 227.001 734.131 227.001ZM734.835 221.785C737.16 221.785 739.069 221.23 740.563 220.121C742.056 218.99 743.155 217.443 743.859 215.481C744.584 213.497 744.947 211.246 744.947 208.729C744.947 206.169 744.584 203.918 743.859 201.977C743.155 200.014 742.067 198.489 740.595 197.401C739.123 196.291 737.256 195.737 734.995 195.737C732.648 195.737 730.707 196.313 729.171 197.465C727.656 198.617 726.525 200.174 725.779 202.137C725.053 204.099 724.691 206.297 724.691 208.729C724.691 211.182 725.064 213.401 725.811 215.385C726.557 217.347 727.677 218.905 729.171 220.057C730.685 221.209 732.573 221.785 734.835 221.785Z" fill="#0FCB8C"/>
                                <path d="M705.265 185.368V179.48H710.961V185.368H705.265ZM705.265 226.04V191.48H710.961V226.04H705.265Z" fill="#0FCB8C"/>
                                <path d="M672 226.04V179.96H698.88V185.656H677.696V200.152H695.04V205.848H677.696V226.04H672Z" fill="#0FCB8C"/>
                                <path d="M646.772 217.56V205.336H634.548V200.632H646.772V188.44H651.476V200.632H663.668V205.336H651.476V217.56H646.772Z" fill="#0FCB8C"/>
                                <path d="M611.312 226.04V191.48H616.4V199.832L615.568 198.744C615.974 197.677 616.497 196.696 617.137 195.8C617.798 194.904 618.534 194.168 619.344 193.592C620.24 192.866 621.254 192.312 622.385 191.928C623.515 191.544 624.667 191.32 625.84 191.256C627.014 191.17 628.113 191.245 629.137 191.48V196.824C627.942 196.504 626.63 196.418 625.201 196.568C623.771 196.717 622.448 197.24 621.232 198.136C620.123 198.925 619.259 199.885 618.641 201.016C618.043 202.146 617.627 203.373 617.393 204.696C617.158 205.997 617.041 207.33 617.041 208.696V226.04H611.312Z" fill="#0FCB8C"/>
                                <path d="M588.476 227.001C585.084 227.001 582.119 226.254 579.58 224.761C577.063 223.246 575.1 221.145 573.692 218.457C572.284 215.747 571.58 212.59 571.58 208.985C571.58 205.209 572.273 201.945 573.66 199.193C575.047 196.419 576.977 194.286 579.452 192.793C581.948 191.278 584.871 190.521 588.22 190.521C591.697 190.521 594.652 191.321 597.084 192.921C599.537 194.521 601.372 196.803 602.588 199.769C603.825 202.734 604.359 206.265 604.188 210.361H598.428V208.313C598.364 204.067 597.5 200.899 595.836 198.809C594.172 196.697 591.697 195.641 588.412 195.641C584.892 195.641 582.215 196.771 580.38 199.033C578.545 201.294 577.628 204.537 577.628 208.761C577.628 212.835 578.545 215.993 580.38 218.233C582.215 220.473 584.828 221.593 588.22 221.593C590.503 221.593 592.487 221.07 594.172 220.025C595.857 218.979 597.18 217.475 598.14 215.513L603.612 217.401C602.268 220.451 600.241 222.819 597.532 224.505C594.844 226.169 591.825 227.001 588.476 227.001ZM575.708 210.361V205.785H601.244V210.361H575.708Z" fill="#0FCB8C"/>
                                <path d="M550.997 227.001C547.754 227.001 545.023 226.201 542.805 224.601C540.586 222.979 538.901 220.793 537.749 218.041C536.618 215.267 536.053 212.163 536.053 208.729C536.053 205.251 536.618 202.137 537.749 199.385C538.901 196.633 540.586 194.467 542.805 192.889C545.045 191.31 547.786 190.521 551.029 190.521C554.229 190.521 556.981 191.321 559.285 192.921C561.61 194.499 563.391 196.665 564.629 199.417C565.866 202.169 566.485 205.273 566.485 208.729C566.485 212.185 565.866 215.289 564.629 218.041C563.391 220.793 561.61 222.979 559.285 224.601C556.981 226.201 554.218 227.001 550.997 227.001ZM535.093 241.401V191.481H540.181V216.921H540.821V241.401H535.093ZM550.293 221.785C552.575 221.785 554.463 221.209 555.957 220.057C557.45 218.905 558.57 217.347 559.317 215.385C560.063 213.401 560.437 211.182 560.437 208.729C560.437 206.297 560.063 204.099 559.317 202.137C558.591 200.174 557.461 198.617 555.925 197.465C554.41 196.313 552.479 195.737 550.133 195.737C547.893 195.737 546.037 196.291 544.565 197.401C543.093 198.489 541.994 200.014 541.269 201.977C540.543 203.918 540.181 206.169 540.181 208.729C540.181 211.246 540.533 213.497 541.237 215.481C541.962 217.443 543.071 218.99 544.565 220.121C546.058 221.23 547.967 221.785 550.293 221.785Z" fill="#0FCB8C"/>
                                <path d="M508.949 227.001C506.368 227.001 504.202 226.531 502.453 225.593C500.725 224.633 499.413 223.374 498.517 221.817C497.642 220.259 497.205 218.553 497.205 216.697C497.205 214.883 497.546 213.315 498.229 211.993C498.933 210.649 499.914 209.539 501.173 208.665C502.432 207.769 503.936 207.065 505.685 206.553C507.328 206.105 509.162 205.721 511.189 205.401C513.237 205.059 515.306 204.75 517.397 204.473C519.488 204.195 521.44 203.929 523.253 203.673L521.205 204.857C521.269 201.785 520.65 199.513 519.349 198.041C518.069 196.569 515.85 195.833 512.693 195.833C510.602 195.833 508.768 196.313 507.189 197.273C505.632 198.211 504.533 199.747 503.893 201.881L498.485 200.249C499.317 197.219 500.928 194.841 503.317 193.113C505.706 191.385 508.853 190.521 512.757 190.521C515.893 190.521 518.581 191.086 520.821 192.217C523.082 193.326 524.704 195.011 525.685 197.273C526.176 198.339 526.485 199.502 526.613 200.761C526.741 201.998 526.805 203.299 526.805 204.665V226.041H521.749V217.753L522.933 218.521C521.632 221.315 519.808 223.427 517.461 224.857C515.136 226.286 512.298 227.001 508.949 227.001ZM509.813 222.233C511.861 222.233 513.632 221.87 515.125 221.145C516.64 220.398 517.856 219.427 518.773 218.233C519.69 217.017 520.288 215.694 520.565 214.265C520.885 213.198 521.056 212.014 521.077 210.713C521.12 209.39 521.141 208.377 521.141 207.673L523.125 208.537C521.269 208.793 519.445 209.038 517.653 209.273C515.861 209.507 514.165 209.763 512.565 210.041C510.965 210.297 509.525 210.606 508.245 210.969C507.306 211.267 506.421 211.651 505.589 212.121C504.778 212.59 504.117 213.198 503.605 213.945C503.114 214.67 502.869 215.577 502.869 216.665C502.869 217.603 503.104 218.499 503.573 219.353C504.064 220.206 504.81 220.899 505.813 221.433C506.837 221.966 508.17 222.233 509.813 222.233Z" fill="#0FCB8C"/>
                                <path d="M461 226.04V179.96H479.208C479.656 179.96 480.189 179.981 480.808 180.024C481.427 180.045 482.024 180.109 482.6 180.216C485.096 180.6 487.187 181.453 488.872 182.776C490.579 184.099 491.859 185.773 492.712 187.8C493.565 189.805 493.992 192.035 493.992 194.488C493.992 196.92 493.555 199.149 492.68 201.176C491.827 203.181 490.547 204.856 488.84 206.2C487.155 207.523 485.075 208.376 482.6 208.76C482.024 208.845 481.427 208.909 480.808 208.952C480.189 208.995 479.656 209.016 479.208 209.016H466.696V226.04H461ZM466.696 203.608H479.016C479.421 203.608 479.88 203.587 480.392 203.544C480.925 203.501 481.427 203.427 481.896 203.32C483.368 202.979 484.563 202.36 485.48 201.464C486.419 200.547 487.101 199.48 487.528 198.264C487.955 197.027 488.168 195.768 488.168 194.488C488.168 193.208 487.955 191.96 487.528 190.744C487.101 189.507 486.419 188.429 485.48 187.512C484.563 186.595 483.368 185.976 481.896 185.656C481.427 185.528 480.925 185.453 480.392 185.432C479.88 185.389 479.421 185.368 479.016 185.368H466.696V203.608Z" fill="#0FCB8C"/>
                            </svg>
                        </Logo>
                    </Box>
                </Toolbar>

                <Divider />

                {/* Thread List */}
                <Box sx={{ p: 2, pb: 0 }}>
                    <Button
                        fullWidth
                        onClick={handleNewThread}
                        disabled={activeThreadId === null}
                        startIcon={!isMobile ? <AddIcon /> : undefined}
                        sx={{
                            justifyContent: 'flex-start',
                            color: '#FFFFFF',
                            bgcolor: 'transparent',
                            border: '1px solid rgba(15, 203, 140, 0.4)',
                            fontWeight: 'normal',
                            textTransform: 'none',
                            mb: 2,
                            py: 0.75,
                            '&:hover': { 
                                bgcolor: 'rgba(15, 203, 140, 0.1)',
                                borderColor: 'rgba(15, 203, 140, 0.6)',
                            },
                            '&.Mui-disabled': {
                                borderColor: 'rgba(15, 203, 140, 0.2)',
                                color: 'rgba(255, 255, 255, 0.3)',
                            }
                        }}
                    >
                        {isMobile ? '+ New' : 'New exploration'}
                    </Button>
                    <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 0.5, px: 1 }}>
                        Recent Threads
                    </Typography>
                </Box>
                <List sx={{ p: 1, flex: 1, overflowY: 'auto' }}>
                    {threads.length === 0 ? (
                        <ListItem>
                            <Typography variant="body2" sx={{ color: '#FFFFFF', opacity: 0.6, fontStyle: 'italic' }}>
                                No threads yet
                            </Typography>
                        </ListItem>
                    ) : (
                        threads
                            .sort((a, b) => b.updatedAt - a.updatedAt) // Most recent first
                            .map((thread) => (
                                <ListItem key={thread.id} disablePadding>
                                    <ThreadButton
                                        selected={thread.id === activeThreadId}
                                        onClick={() => handleSelectThread(thread.id)}
                                    >
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                overflow: 'hidden', 
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {thread.title}
                                        </Typography>
                                    </ThreadButton>
                                </ListItem>
                            ))
                    )}
                </List>
            </Drawer>

            {/* Main Content Area */}
            <MainContent>
                {/* Top Right Links */}
                <TopRightLinks>
                    <Link
                        href="https://chromewebstore.google.com/detail/asta/baigocfaeehaielhlieikbgahomikbdj"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#0FCB8C',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Asta Google Chrome Extension
                    </Link>
                    <Link
                        href="https://allenai.org/blog/asta"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#0FCB8C',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        About
                    </Link>
                    <Link
                        href="https://docs.google.com/forms/d/e/1FAIpQLSd1nFbMd2jOqItp3kNtdYujsngjUgHZypFbizikfgX2GBJZZg/viewform?usp=sharing&ouid=102647895654063825924"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#0FCB8C',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Feedback
                    </Link>
                    <AuthButton />
                </TopRightLinks>
                <Home 
                    activeThread={activeThread}
                    onThreadsUpdate={handleThreadsUpdate}
                    onThreadSelect={setActiveThreadId}
                    onUpdateThread={handleUpdateThreadInPlace}
                    focusPaperTitleInput={focusPaperTitleInput}
                />
                <Footer />
            </MainContent>
        </Box>
    );

    // Return authenticated app content with terms modal if needed
    return (
        <>
            {appContent}
            {termsAccepted === false && (
                <TermsAndConditionsModal open={true} onAccept={handleTermsAccept} />
            )}
        </>
    );
};

// Main App component - handles routing
export const App = () => {
    return (
        <Routes>
            {/* Public landing page */}
            <Route path="/" element={<Landing />} />
            <Route path="/landing" element={<Landing />} />
            
            {/* Protected app routes */}
            <Route path="/app/*" element={<AuthenticatedApp />} />
        </Routes>
    );
};

const ThreadButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>`
    && {
        width: 100%;
        margin-bottom: ${({ theme }) => theme.spacing(0.5)};
        background-color: ${({ selected }) =>
            selected ? '#0FCB8C' : 'transparent'};
        color: ${({ selected }) =>
            selected ? '#032629' : '#FFFFFF'};
        border-radius: 4px;

        &:hover {
            background-color: ${({ selected }) =>
                selected ? '#0FCB8C' : 'rgba(15, 203, 140, 0.1)'};
        }
    }
`;

const MainContent = styled(Box)`
    && {
        flex-grow: 1;
        padding: ${({ theme }) => theme.spacing(3)};
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        background-color: ${({ theme }) => theme.palette.background.default};
        color: ${({ theme }) => theme.palette.text.primary};

        ${({ theme }) => theme.breakpoints.down('sm')} {
            padding: ${({ theme }) => theme.spacing(2)};
        }

        ${({ theme }) => theme.breakpoints.down('xs')} {
            padding: ${({ theme }) => theme.spacing(1)};
        }
    }
`;

const Logo = styled('div')`
    width: 240px;
    height: auto;
    padding-top: 8px;
    svg {
        width: 100%;
        height: auto;
        display: block;
    }
`;

const TopRightLinks = styled(Box)`
    && {
        position: absolute;
        top: ${({ theme }) => theme.spacing(2)};
        right: ${({ theme }) => theme.spacing(3)};
        display: flex;
        gap: ${({ theme }) => theme.spacing(3)};
        z-index: 1;

        ${({ theme }) => theme.breakpoints.down('sm')} {
            top: ${({ theme }) => theme.spacing(1)};
            right: ${({ theme }) => theme.spacing(2)};
            gap: ${({ theme }) => theme.spacing(2)};
        }
    }
`;
